<script setup lang="ts">
  import HttpError from '@leaflink/stash/HttpError.vue';
  import Loading from '@leaflink/stash/Loading.vue';
  import debounce from 'lodash-es/debounce';
  import { storeToRefs } from 'pinia';
  import { computed, onBeforeUnmount, onMounted, ref, useCssModule, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';

  import GlobalBanner from '@/components/layout/GlobalBanner';
  import SignupDirectPay from '@/components/SignupDirectPay/SignupDirectPay.vue';
  import { useErrorPage } from '@/composables/useErrorPage';
  import { DEBOUNCE, MOUNT_POINT_CLASS as MOUNT_POINT_CLASS_RAW, SPLIT_NAMES } from '@/constants';
  import { supportedErrorPageCodes } from '@/constants/errors';
  import { useAuthStore } from '@/piniaStores/auth/auth.store';
  import { useUserStore } from '@/piniaStores/user/user.store';
  import { isVueRoute } from '@/router/utils';
  import { createSession } from '@/services/session';
  import { notifyPendingSystemNofications } from '@/services/systemNotifications/systemNotificationsService';
  import { isHttpError } from '@/utils/errors';

  import AppHeader from './AppHeader.vue';
  import AppLogoBurger from './AppLogoBurger.vue';
  import AppSidebar from './AppSidebar.vue';
  import HijackBanner from './HijackBanner.vue';

  defineOptions({ inheritAttrs: false });

  const emit = defineEmits<{
    (e: 'mount'): void;
  }>();

  const userStore = useUserStore();
  const authStore = useAuthStore();
  const route = useRoute();
  const router = useRouter();
  const httpSession = createSession('');
  const errorPage = useErrorPage();

  const { getIsSplitEnabled, user } = storeToRefs(userStore);

  const classes = useCssModule();
  const contentElement = ref<HTMLElement | null>(null); // TODO: remove during https://leaflink.atlassian.net/browse/MPFE-240
  const isSidebarOpen = ref(false);
  const isLegacy = computed(() => (route ? !isVueRoute(route) : false));
  const MOUNT_POINT_CLASS = MOUNT_POINT_CLASS_RAW.substring(1);
  const cachedViewportWidth = ref(window.innerWidth);
  const ssrIframeElement = ref<HTMLIFrameElement>();
  const isSsrIframeLoading = ref(true);

  const ssrIframeSrc = computed(() => {
    const src = new URL(`${window.location.origin}${route.fullPath}`);

    if (import.meta.env.VITE_SSR_IFRAME_QUERY_KEY && import.meta.env.VITE_SSR_IFRAME_QUERY_VALUE) {
      src.searchParams.set(import.meta.env.VITE_SSR_IFRAME_QUERY_KEY, import.meta.env.VITE_SSR_IFRAME_QUERY_VALUE);
    }

    return src.toString();
  });

  const legacyPointerEventsClass = computed(() => {
    return isLegacy.value ? classes['pointer-auto'] : '';
  });

  const onResizeDebounced = debounce(() => {
    // If the resize was horizontal, we might need to close the sidebar (not sure why).
    if (window.innerWidth !== cachedViewportWidth.value) {
      cachedViewportWidth.value = window.innerWidth;
      isSidebarOpen.value = false;
    }
  }, DEBOUNCE.FAST);

  watch(isSidebarOpen, (newVal, oldVal) => {
    if (newVal !== oldVal) {
      document.body.classList.toggle('is-sidebar-open', newVal);
    }
  });

  watch(
    () => route?.fullPath,
    (newFullPath, oldFullPath) => {
      if (newFullPath !== oldFullPath) {
        isSsrIframeLoading.value = true;
      }
    },
  );

  /**
   * The following code block handles error responses from the SSR iframe.
   * The 'ssr-iframe-loaded' event handles successful (non-error) responses.
   */
  async function onSsrIframeLoad() {
    errorPage.settings = undefined;

    try {
      try {
        await httpSession.get(ssrIframeSrc.value);
      } catch (error) {
        if (isHttpError(error)) {
          const status = supportedErrorPageCodes.find((code) => code === error.response?.status);

          errorPage.settings = { errorCode: status || 404 };
        } else {
          throw error;
        }
      }
    } finally {
      isSsrIframeLoading.value = false;
    }
  }

  watch(
    () => ssrIframeElement.value,
    () => {
      if (!ssrIframeElement.value) {
        return;
      }

      ssrIframeElement.value.removeEventListener('load', onSsrIframeLoad);
      ssrIframeElement.value.addEventListener('load', onSsrIframeLoad);
    },
    { immediate: true },
  );

  onMounted(() => {
    contentElement.value = document.getElementById('content');

    window.addEventListener('resize', onResizeDebounced);

    emit('mount');

    // Prevent Django page content from rendering before the app frame is done rendering.
    if (isLegacy.value && contentElement.value && !getIsSplitEnabled.value(SPLIT_NAMES.SSR_IFRAME)) {
      contentElement.value.classList.remove('invisible');
    }

    if (getIsSplitEnabled.value(SPLIT_NAMES.SSR_IFRAME)) {
      ssrIframeElement.value?.contentWindow?.addEventListener('beforeunload', () => {
        isSsrIframeLoading.value = true;
      });

      // See: https://developer.mozilla.org/en-US/docs/Web/API/Window/postMessage
      window.addEventListener('message', async function (event) {
        if (
          event.origin !== window.location.origin ||
          !ssrIframeElement.value?.contentWindow ||
          ssrIframeElement.value.id !== 'ssr-iframe'
        ) {
          return; // for security and performance purposes
        }

        const eventId = event.data?.id;
        const eventPageTitle = event.data?.pageTitle;
        const eventUrlFullPath = event.data?.urlFullPath;

        // This event comes from postMessage('ssr-iframe-loaded') within the Django codebase
        if (eventId === 'ssr-iframe-loaded') {
          if (eventPageTitle) {
            document.title = eventPageTitle;
          }
          const currentTopUrl = new URL(window.location.href);
          const currentIframeUrl = new URL(ssrIframeElement.value.contentWindow?.location.href);

          // Remove trailing slashes directly
          if (currentTopUrl.pathname.endsWith('/')) {
            currentTopUrl.pathname = currentTopUrl.pathname.slice(0, -1);
          }
          if (currentIframeUrl.pathname.endsWith('/')) {
            currentIframeUrl.pathname = currentIframeUrl.pathname.slice(0, -1);
          }

          currentIframeUrl.searchParams.delete(import.meta.env.VITE_SSR_IFRAME_QUERY_KEY || 'mp_ssr');
          if (currentTopUrl.href !== currentIframeUrl.href) {
            router.replace(currentIframeUrl.pathname + currentIframeUrl.search + currentIframeUrl.hash);
          }

          // Lazy loading is used for initSsrVueApps to avoid circular dependencies and reduce bundle size
          const { initSsrVueApps } = await import('@/utils/initSsrVueApps');

          // `ssrIframeElement.value` will be `null` when it comes from the `vue/index.html` Django template
          if (ssrIframeElement.value) {
            initSsrVueApps(ssrIframeElement.value.contentWindow);
          }

          notifyPendingSystemNofications(); // needed for when the iframe reloads with the same URL; otherwise, router/index.ts calls this on page navigation

          return;
        }

        // The `vue/index.html` Django template emits this event. Some Django views redirect to a view that returns this template.
        // When Django responds with this template, the intent is to render a Vue page; therefore, we should let Vue Router find and render the matching Vue page.
        if (eventId === 'ssr-vue-template') {
          const eventUrl = new URL(window.location.origin + eventUrlFullPath);

          eventUrl.searchParams.delete(import.meta.env.VITE_SSR_IFRAME_QUERY_KEY || 'mp_ssr');

          const eventPathWithoutMpSsr = eventUrl.pathname + eventUrl.search + eventUrl.hash;

          if (route.fullPath !== eventPathWithoutMpSsr) {
            router.replace(eventPathWithoutMpSsr);
          }

          return;
        }

        // This event comes from postMessage('ssr-iframe-navigated') within the Django codebase
        if (eventId === 'ssr-iframe-navigated') {
          if (eventUrlFullPath.startsWith('/hijack/acquire/')) {
            await httpSession.get(eventUrlFullPath, { params: { next: null } }); // TODO: hijacking with AJAX is a security risk; find another way to hijack or stop allowing hijacking
            window.location.assign('/');

            return;
          }

          if (route.fullPath !== eventUrlFullPath) {
            document.title = ssrIframeElement.value.title;
            router.push(eventUrlFullPath);
          }

          return;
        }
      });
    }
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', onResizeDebounced);
  });
</script>

<template>
  <div
    :class="[
      $style.frame,
      {
        [`${$style['frame-legacy']} ${$style['pointer-none']}`]: isLegacy && !getIsSplitEnabled(SPLIT_NAMES.SSR_IFRAME),
      },
    ]"
    v-bind="$attrs"
  >
    <AppLogoBurger
      :class="legacyPointerEventsClass"
      @toggle="isSidebarOpen = !isSidebarOpen"
      @navigate="isSidebarOpen = false"
    />
    <AppHeader id="header" :class="legacyPointerEventsClass" />
    <div :class="[$style.overlay, legacyPointerEventsClass]" @click="isSidebarOpen = !isSidebarOpen"></div>
    <AppSidebar
      id="sidebar"
      :class="legacyPointerEventsClass"
      :is-open="isSidebarOpen"
      @navigate="isSidebarOpen = false"
      @dismiss="isSidebarOpen = false"
    />

    <main
      v-if="getIsSplitEnabled(SPLIT_NAMES.SSR_IFRAME) ? true : !isLegacy"
      id="app-content"
      :class="[
        $style['app-content'],
        {
          'tw-pb-20': getIsSplitEnabled(SPLIT_NAMES.SSR_IFRAME) && !isLegacy && !errorPage.isVisible,
          'tw-pb-[5.75rem]': !getIsSplitEnabled(SPLIT_NAMES.SSR_IFRAME),
        },
      ]"
    >
      <div class="tw-h-full tw-flex tw-flex-col">
        <HijackBanner v-if="user.adminId" />
        <GlobalBanner />
        <template v-if="isLegacy && getIsSplitEnabled(SPLIT_NAMES.SSR_IFRAME)">
          <div v-if="isSsrIframeLoading" class="tw-w-full tw-h-full tw-grid tw-place-content-center">
            <Loading />
          </div>
          <!-- the :key attribute prevents duplicate location history entries -->
          <iframe
            id="ssr-iframe"
            data-test="ssr-iframe"
            ref="ssrIframeElement"
            :key="route.fullPath"
            :src="ssrIframeSrc"
            class="tw-flex-1 tw-w-full"
            :class="{ 'tw-hidden': isSsrIframeLoading || errorPage.isVisible }"
          ></iframe>
        </template>
        <router-view v-else-if="!errorPage.isVisible" />
        <div
          v-if="
            errorPage.isVisible && (isLegacy && getIsSplitEnabled(SPLIT_NAMES.SSR_IFRAME) ? !isSsrIframeLoading : true)
          "
          class="tw-p-gutter tw-h-full"
        >
          <HttpError
            v-bind="errorPage.settings"
            class="tw-rounded tw-shadow tw-border"
            @logout="errorPage.settings?.onLogout || authStore.logout()"
          />
        </div>
      </div>
    </main>

    <!-- This is apparently used as a mounting point for the Stash dropdown on non-legacy pages  -->
    <div v-if="!isLegacy" :class="MOUNT_POINT_CLASS"></div>
  </div>

  <SignupDirectPay />
</template>

<style>
  appcues-experience-container {
    position: absolute !important;
    width: 100%;
  }
</style>

<style lang="scss" module>
  .frame {
    display: grid;
    grid-gap: 0;
    grid-template-areas:
      'header'
      'content'
      'footer';
    grid-template-columns: 1fr;
    grid-template-rows: var(--height-topbar) 1fr;
    height: 100vh;
    position: relative;
    z-index: z-index(
      'page'
    ); // this is necessary to create a new stacking context, which ensures the mini cart and sidebar cover the main app content

    @include breakpoint('xl') {
      grid-template-areas:
        'sidebar header'
        'sidebar content'
        'sidebar footer';
      grid-template-columns: var(--sidebar-width) 1fr;
    }
  }

  .app-content {
    grid-area: content;
  }

  // #region For django pages only; delete as part of https://leaflink.atlassian.net/browse/MPFE-240

  .frame-legacy {
    position: fixed;
    width: 100vw;
  }

  .pointer-auto {
    pointer-events: auto;
  }

  .pointer-none {
    pointer-events: none;
  }

  // #endregion

  :global(.is-sidebar-open) {
    overflow: hidden;
    overscroll-behavior-y: contain;
    touch-action: none; /* prevent scrolling */
  }

  .overlay {
    @include cover;

    background-color: rgb(0 0 0 / 38%);
    opacity: 0;
    transition: opacity $secondary-transition;
    visibility: hidden;
    z-index: z-index('behind');

    :global(.is-sidebar-open) & {
      opacity: 1;
      visibility: visible;
      z-index: z-index('page'); /* this needs to be lower than the z-index of the AppLogoBurger */
    }
  }
</style>
